import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  QA_CATEGORY_LABEL,
  QA_CATEGORY_ORDER,
  QA_PASS_AT,
  QA_REVIEW_AT,
  RUBRIC_PASS_AT,
} from '@platform/schema/qa-score';
import { useState } from 'react';

import type { SecondModelFailure } from '@platform/schema/generation';
import type { LessonWorkspace } from '@platform/schema/review';

/**
 * What the reviewer thinks of this lesson, in one panel — GEN-89, GEN-93.
 *
 * The six numbers are the point rather than the blend. A lesson averages its way to a
 * good overall score while one category is unreliable, and grounding is the category a
 * reader cannot check by looking: so the panel shows all six, and the verdict beside the
 * blend is the API's, which already refuses to hide a bad category behind a good average.
 */

type Qa = LessonWorkspace['qa'];

const ORDER = QA_CATEGORY_ORDER;

export const STATUS: Record<
  Qa['status'],
  { label: string; colour: 'success' | 'warning' | 'error' }
> = {
  PASSED: { label: 'Passed', colour: 'success' },
  NEEDS_REVIEW: { label: 'Needs review', colour: 'warning' },
  FAILED: { label: 'Failed QA', colour: 'error' },
};

/**
 * Why the second model gave no answer — GEN-166. A row saved before the reason was
 * stored reads as unreachable.
 */
const FAILED_BECAUSE: Record<SecondModelFailure, string> = {
  OUT_OF_CREDITS: 'The second model is out of credits',
  UNREACHABLE: 'The second model could not be reached',
  TIMED_OUT: 'The second model timed out',
};

/** One score's own colour, on the same two lines the verdict uses. */
const colourOf = (score: number): 'success' | 'warning' | 'error' =>
  score < QA_REVIEW_AT ? 'error' : score < QA_PASS_AT ? 'warning' : 'success';

export function Scorecard({ qa }: { qa: Qa }) {
  const verdict = STATUS[qa.status];
  const [explaining, setExplaining] = useState(false);
  // The SDK types a nullable number as `unknown`, so it is narrowed here.
  const rubric =
    qa.rubric === undefined
      ? undefined
      : typeof qa.rubric === 'number'
        ? qa.rubric
        : null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}
        >
          <Typography variant="subtitle2">QA score</Typography>
          <IconButton
            size="small"
            aria-label="How we got this score"
            aria-expanded={explaining}
            aria-controls="qa-breakdown"
            onClick={() => {
              setExplaining(!explaining);
            }}
          >
            ⓘ
          </IconButton>
          <Typography variant="h5">{qa.overall} / 100</Typography>
          <Chip size="small" label={verdict.label} color={verdict.colour} />
        </Stack>

        <Collapse in={explaining} id="qa-breakdown">
          <Breakdown qa={qa} />
        </Collapse>

        {ORDER.map((category) => {
          const score = qa.scores[category];
          const unchecked = category === 'GROUNDING' && qa.cited === false;

          return (
            <Stack key={category} spacing={0.5}>
              <Stack
                direction="row"
                sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <Typography variant="body2">
                  {QA_CATEGORY_LABEL[category]}
                </Typography>
                <Typography
                  variant="body2"
                  color={unchecked ? 'warning.main' : 'text.secondary'}
                >
                  {unchecked ? 'Not checked' : score}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={unchecked ? 0 : score}
                color={unchecked ? 'warning' : colourOf(score)}
                aria-label={QA_CATEGORY_LABEL[category]}
              />
            </Stack>
          );
        })}

        {qa.cited === false && (
          <Typography variant="body2" color="text.secondary">
            This lesson cites no source, so its facts were not checked against
            one. Add a source, or read them yourself.
          </Typography>
        )}

        {/* GEN-100. Absent when the second model was never asked, so a lesson reviewed
            before it existed does not read as a gap. */}
        {rubric !== undefined && (
          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}
          >
            <Typography variant="body2">Lesson-design rubric</Typography>
            <Typography
              variant="body2"
              color={
                rubric === null || rubric < RUBRIC_PASS_AT
                  ? 'warning.main'
                  : 'text.secondary'
              }
            >
              {rubric === null ? 'Not scored' : `${String(rubric)} / 100`}
            </Typography>
          </Stack>
        )}

        {rubric === null && (
          <Typography variant="body2" color="text.secondary">
            {FAILED_BECAUSE[qa.rubricFailure ?? 'UNREACHABLE']}, so read this
            lesson against the lesson-design rules yourself.
          </Typography>
        )}

        {qa.claimsFailure !== undefined && (
          <Typography variant="body2" color="text.secondary">
            {FAILED_BECAUSE[qa.claimsFailure]}, so it did not check this
            lesson&apos;s facts against its source. Check them yourself.
          </Typography>
        )}

        {/* Why a lesson can read FAILED with six good scores. The publish gate files
            findings too, and those name no category — so without this line the panel
            would show a verdict nothing on it explains. */}
        {qa.blockers > 0 && (
          <Typography variant="body2" color="error">
            {qa.blockers} unresolved blocker{qa.blockers === 1 ? '' : 's'}, so
            the lesson fails whatever the blend says.
          </Typography>
        )}

        {qa.uncategorised > 0 && (
          <Typography variant="body2" color="text.secondary">
            {qa.uncategorised} finding{qa.uncategorised === 1 ? '' : 's'} named
            no category, so no score moved for{' '}
            {qa.uncategorised === 1 ? 'it' : 'them'}.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

const plural = (n: number, one: string, many = `${one}s`): string =>
  `${String(n)} ${n === 1 ? one : many}`;

const percent = (weight: number): string =>
  `${String(Math.round(weight * 100))}%`;

type Reason = Qa['breakdown']['reasons'][number];

/** One tripped rule in words. Every number is the API's. */
function said(reason: Reason): string {
  const value = String(reason.value ?? '');
  const line = String(reason.line ?? '');
  switch (reason.rule) {
    case 'OPEN_BLOCKERS':
      return plural(reason.value ?? 0, 'open blocker');
    case 'CATEGORY_BELOW':
      return `${reason.category === undefined ? 'A category' : QA_CATEGORY_LABEL[reason.category]} is ${value}, below ${line}`;
    case 'BLEND_BELOW':
      return `the QA score is ${value}, below ${line}`;
    case 'RUBRIC_BELOW':
      return `the lesson-design rubric is ${value}, below ${line}`;
    case 'RUBRIC_UNSCORED':
      return "the lesson-design rubric wasn't scored";
    case 'UNCITED':
      return 'the lesson cites no source';
  }
}

/** A no-op when none is on the page: the workspace caps the findings it lists. */
const showFindings = (category: string) => {
  document
    .querySelector(`[data-qa-category="${category}"]`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

/**
 * How this lesson's score was worked out — GEN-174. Renders `breakdown` and nothing
 * else, so it cannot disagree with the formula that produced the numbers above it.
 */
function Breakdown({ qa }: { qa: Qa }) {
  const { costs, categories, blend, passAt, reviewAt, rubricPassAt, reasons } =
    qa.breakdown;

  return (
    <Stack spacing={1} sx={{ pt: 1 }}>
      <Typography variant="body2">
        Each category starts at 100. An open blocker costs {costs.BLOCKER}, a
        warning {costs.WARN} and a note {costs.NOTE}. A score stops at 0. Only
        open problems count. Accepted and dismissed ones don&apos;t, but
        proposed fixes still do.
      </Typography>

      {categories.map((row) => {
        const open = row.blockers + row.warnings + row.notes;
        const terms = [
          [row.blockers, 'blocker', costs.BLOCKER],
          [row.warnings, 'warning', costs.WARN],
          [row.notes, 'note', costs.NOTE],
        ] as const;
        return (
          <Stack
            key={row.category}
            direction="row"
            spacing={1}
            sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}
            data-testid="qa-breakdown-row"
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {QA_CATEGORY_LABEL[row.category]}:
            </Typography>
            <Typography variant="body2">
              100
              {terms
                .filter(([n]) => n > 0)
                .map(
                  ([n, name, cost]) =>
                    ` − ${plural(n, name)} × ${String(cost)}`,
                )
                .join('')}{' '}
              = {row.score}
              {row.lost > 100 ? ' (it stops at 0)' : ''}
              {row.category === 'GROUNDING' && qa.cited === false
                ? ' (shown as "Not checked" above because the lesson cites no source, but it still counts)'
                : ''}
            </Typography>
            {open > 0 && (
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  showFindings(row.category);
                }}
              >
                See {open === 1 ? 'it' : 'them'}
              </Link>
            )}
          </Stack>
        );
      })}

      {qa.uncategorised > 0 && (
        <Typography variant="body2">
          {qa.uncategorised === 1
            ? "1 open problem has no category. It moves no score, but if it's a blocker, it still fails the lesson."
            : `${String(qa.uncategorised)} open problems have no category. They move no score, but a blocker among them still fails the lesson.`}
        </Typography>
      )}

      <Typography variant="body2" data-testid="qa-breakdown-blend">
        QA score:{' '}
        {categories
          .map((row) => `${percent(row.weight)} × ${String(row.score)}`)
          .join(' + ')}{' '}
        = {blend}
        {blend === qa.overall ? '' : `, rounded to ${String(qa.overall)}`}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        A lesson fails with any open blocker, or with any category or the QA
        score below {reviewAt}. It passes at {passAt} and above. In between, it
        needs review. The lesson-design rubric isn&apos;t part of the score, but
        below {rubricPassAt} it sends the lesson to review.
      </Typography>

      {qa.status === 'PASSED' ? (
        <Typography
          variant="body2"
          color="success.main"
          data-testid="qa-reason"
        >
          Passed: no open blockers, and every category and the QA score are{' '}
          {passAt} or above.
        </Typography>
      ) : (
        reasons.map((reason) => (
          <Typography
            key={`${reason.rule}-${reason.category ?? ''}`}
            variant="body2"
            color={`${STATUS[qa.status].colour}.main`}
            data-testid="qa-reason"
          >
            {qa.status === 'FAILED' ? 'Failed' : 'Needs review'}: {said(reason)}
          </Typography>
        ))
      )}
    </Stack>
  );
}
